import { Sport } from '../types/core';

/**
 * Sport-specific statistic definitions
 * Each sport has a list of available stat keys that coaches can record
 */
export const SPORT_STAT_KEYS: Record<Sport, Array<{ key: string; label: string; description?: string }>> = {
  Baseball: [
    { key: '1B', label: 'Singles (1B)', description: 'Single base hits' },
    { key: '2B', label: 'Doubles (2B)', description: 'Double base hits' },
    { key: '3B', label: 'Triples (3B)', description: 'Triple base hits' },
    { key: 'HR', label: 'Home Runs (HR)', description: 'Home runs' },
    { key: 'BB', label: 'Walks (BB)', description: 'Base on balls (batting)' },
    { key: 'SO', label: 'Strikeouts (SO)', description: 'Strikeouts (batting)' },
    { key: 'SB', label: 'Stolen Bases (SB)', description: 'Stolen bases' },
    { key: 'RBI', label: 'Runs Batted In (RBI)', description: 'Runs batted in' },
    { key: 'R', label: 'Runs (R)', description: 'Runs scored' },
    { key: 'H', label: 'Hits (H)', description: 'Total hits (batting)' },
    { key: 'AB', label: 'At Bats (AB)', description: 'At bats' },
    { key: 'HBP', label: 'Hit By Pitch (HBP)', description: 'Hit by pitch' },
    { key: 'SF', label: 'Sacrifice Fly (SF)', description: 'Sacrifice fly' },
    { key: 'IP', label: 'Innings Pitched (IP)', description: 'Innings pitched' },
    { key: 'K', label: 'Strikeouts (K)', description: 'Pitching strikeouts' },
    { key: 'W', label: 'Wins (W)', description: 'Pitching wins' },
    { key: 'L', label: 'Losses (L)', description: 'Pitching losses' },
    { key: 'SV', label: 'Saves (SV)', description: 'Saves' },
    { key: 'ERA', label: 'ERA', description: 'Earned run average' },
    { key: 'H_Allowed', label: 'Hits Allowed', description: 'Hits allowed (pitching)' },
    { key: 'BB_Allowed', label: 'Walks Allowed', description: 'Walks allowed (pitching)' },
    { key: 'R_Allowed', label: 'Runs Allowed', description: 'Runs allowed (pitching)' },
    { key: 'ER', label: 'Earned Runs (ER)', description: 'Earned runs allowed (pitching)' },
    { key: 'BF', label: 'Batters Faced (BF)', description: 'Batters faced (pitching)' },
    { key: 'Balls', label: 'Balls', description: 'Balls thrown (pitch count)' },
    { key: 'Strikes', label: 'Strikes', description: 'Strikes thrown (pitch count)' },
    { key: 'Pitches', label: 'Pitches', description: 'Total pitches thrown' },
    { key: 'HBP_Allowed', label: 'Hit Batters (HBP)', description: 'Hit batters (pitching)' },
    { key: 'WP', label: 'Wild Pitches (WP)', description: 'Wild pitches' },
    { key: 'Balk', label: 'Balks', description: 'Balks' },
  ],
  Softball: [
    { key: '1B', label: 'Singles (1B)', description: 'Single base hits' },
    { key: '2B', label: 'Doubles (2B)', description: 'Double base hits' },
    { key: '3B', label: 'Triples (3B)', description: 'Triple base hits' },
    { key: 'HR', label: 'Home Runs (HR)', description: 'Home runs' },
    { key: 'BB', label: 'Walks (BB)', description: 'Base on balls (batting)' },
    { key: 'SO', label: 'Strikeouts (SO)', description: 'Strikeouts (batting)' },
    { key: 'SB', label: 'Stolen Bases (SB)', description: 'Stolen bases' },
    { key: 'RBI', label: 'Runs Batted In (RBI)', description: 'Runs batted in' },
    { key: 'R', label: 'Runs (R)', description: 'Runs scored' },
    { key: 'H', label: 'Hits (H)', description: 'Total hits (batting)' },
    { key: 'AB', label: 'At Bats (AB)', description: 'At bats' },
    { key: 'HBP', label: 'Hit By Pitch (HBP)', description: 'Hit by pitch' },
    { key: 'SF', label: 'Sacrifice Fly (SF)', description: 'Sacrifice fly' },
    { key: 'IP', label: 'Innings Pitched (IP)', description: 'Innings pitched' },
    { key: 'K', label: 'Strikeouts (K)', description: 'Pitching strikeouts' },
    { key: 'W', label: 'Wins (W)', description: 'Pitching wins' },
    { key: 'L', label: 'Losses (L)', description: 'Pitching losses' },
    { key: 'SV', label: 'Saves (SV)', description: 'Saves' },
    { key: 'ERA', label: 'ERA', description: 'Earned run average' },
    { key: 'H_Allowed', label: 'Hits Allowed', description: 'Hits allowed (pitching)' },
    { key: 'BB_Allowed', label: 'Walks Allowed', description: 'Walks allowed (pitching)' },
    { key: 'R_Allowed', label: 'Runs Allowed', description: 'Runs allowed (pitching)' },
    { key: 'ER', label: 'Earned Runs (ER)', description: 'Earned runs allowed (pitching)' },
    { key: 'BF', label: 'Batters Faced (BF)', description: 'Batters faced (pitching)' },
    { key: 'Balls', label: 'Balls', description: 'Balls thrown (pitch count)' },
    { key: 'Strikes', label: 'Strikes', description: 'Strikes thrown (pitch count)' },
    { key: 'Pitches', label: 'Pitches', description: 'Total pitches thrown' },
    { key: 'HBP_Allowed', label: 'Hit Batters (HBP)', description: 'Hit batters (pitching)' },
    { key: 'WP', label: 'Wild Pitches (WP)', description: 'Wild pitches' },
    { key: 'Balk', label: 'Balks', description: 'Balks' },
  ],
  Football: [
    { key: 'TD', label: 'Touchdowns (TD)', description: 'Touchdowns scored' },
    { key: 'PassYds', label: 'Passing Yards', description: 'Passing yards' },
    { key: 'PassTD', label: 'Passing TDs', description: 'Passing touchdowns' },
    { key: 'PassInt', label: 'Interceptions Thrown', description: 'Interceptions thrown' },
    { key: 'RushYds', label: 'Rushing Yards', description: 'Rushing yards' },
    { key: 'RushTD', label: 'Rushing TDs', description: 'Rushing touchdowns' },
    { key: 'RecYds', label: 'Receiving Yards', description: 'Receiving yards' },
    { key: 'RecTD', label: 'Receiving TDs', description: 'Receiving touchdowns' },
    { key: 'Rec', label: 'Receptions', description: 'Number of receptions' },
    { key: 'Tackles', label: 'Tackles', description: 'Total tackles' },
    { key: 'Sacks', label: 'Sacks', description: 'Quarterback sacks' },
    { key: 'Int', label: 'Interceptions', description: 'Interceptions caught' },
    { key: 'Fumbles', label: 'Fumbles', description: 'Fumbles' },
    { key: 'FumbleRec', label: 'Fumble Recoveries', description: 'Fumble recoveries' },
    { key: 'FG', label: 'Field Goals', description: 'Field goals made' },
    { key: 'XP', label: 'Extra Points', description: 'Extra points made' },
  ],
  FlagFootball: [
    { key: 'TD', label: 'Touchdowns (TD)', description: 'Touchdowns scored' },
    { key: 'PassYds', label: 'Passing Yards', description: 'Passing yards' },
    { key: 'PassTD', label: 'Passing TDs', description: 'Passing touchdowns' },
    { key: 'PassInt', label: 'Interceptions Thrown', description: 'Interceptions thrown' },
    { key: 'RushYds', label: 'Rushing Yards', description: 'Rushing yards' },
    { key: 'RushTD', label: 'Rushing TDs', description: 'Rushing touchdowns' },
    { key: 'RecYds', label: 'Receiving Yards', description: 'Receiving yards' },
    { key: 'RecTD', label: 'Receiving TDs', description: 'Receiving touchdowns' },
    { key: 'Rec', label: 'Receptions', description: 'Number of receptions' },
    { key: 'Tackles', label: 'Tackles', description: 'Flag pulls/tackles' },
    { key: 'Int', label: 'Interceptions', description: 'Interceptions caught' },
    { key: 'Fumbles', label: 'Fumbles', description: 'Fumbles' },
  ],
  Basketball: [
    { key: 'Points', label: 'Points', description: 'Total points scored' },
    { key: 'Rebounds', label: 'Rebounds', description: 'Total rebounds' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Steals', label: 'Steals', description: 'Steals' },
    { key: 'Blocks', label: 'Blocks', description: 'Blocked shots' },
    { key: '3PT', label: '3-Pointers Made', description: 'Three-point field goals made' },
    { key: 'FGM', label: 'Field Goals Made', description: 'Field goals made' },
    { key: 'FGA', label: 'Field Goals Attempted', description: 'Field goals attempted' },
    { key: 'FTM', label: 'Free Throws Made', description: 'Free throws made' },
    { key: 'FTA', label: 'Free Throws Attempted', description: 'Free throws attempted' },
    { key: 'Turnovers', label: 'Turnovers', description: 'Turnovers' },
    { key: 'Fouls', label: 'Fouls', description: 'Personal fouls' },
  ],
  Soccer: [
    { key: 'Goals', label: 'Goals', description: 'Goals scored' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Shots', label: 'Shots', description: 'Shots taken' },
    { key: 'ShotsOnGoal', label: 'Shots on Goal', description: 'Shots on target' },
    { key: 'Saves', label: 'Saves', description: 'Goalkeeper saves' },
    { key: 'YellowCards', label: 'Yellow Cards', description: 'Yellow cards received' },
    { key: 'RedCards', label: 'Red Cards', description: 'Red cards received' },
    { key: 'Tackles', label: 'Tackles', description: 'Tackles made' },
    { key: 'Interceptions', label: 'Interceptions', description: 'Interceptions' },
  ],
  Hockey: [
    { key: 'Goals', label: 'Goals', description: 'Goals scored' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Points', label: 'Points', description: 'Total points (goals + assists)' },
    { key: 'Shots', label: 'Shots', description: 'Shots on goal' },
    { key: 'Saves', label: 'Saves', description: 'Goalkeeper saves' },
    { key: 'PIM', label: 'Penalty Minutes', description: 'Penalty minutes' },
    { key: 'Hits', label: 'Hits', description: 'Hits delivered' },
    { key: 'Blocks', label: 'Blocks', description: 'Shots blocked' },
  ],
  Volleyball: [
    { key: 'Kills', label: 'Kills', description: 'Kills' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Aces', label: 'Aces', description: 'Service aces' },
    { key: 'Digs', label: 'Digs', description: 'Digs' },
    { key: 'Blocks', label: 'Blocks', description: 'Blocks' },
    { key: 'Errors', label: 'Errors', description: 'Errors' },
  ],
  Lacrosse: [
    { key: 'Goals', label: 'Goals', description: 'Goals scored' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Points', label: 'Points', description: 'Total points (goals + assists)' },
    { key: 'Shots', label: 'Shots', description: 'Shots taken' },
    { key: 'Saves', label: 'Saves', description: 'Goalkeeper saves' },
    { key: 'GroundBalls', label: 'Ground Balls', description: 'Ground balls recovered' },
    { key: 'Turnovers', label: 'Turnovers', description: 'Turnovers' },
  ],
};

/**
 * Get available stat keys for a sport
 */
export function getStatKeysForSport(sport: Sport): Array<{ key: string; label: string; description?: string }> {
  return SPORT_STAT_KEYS[sport] || [];
}

